// Step 1 — Sign-up form (Figma 3/4). "Tell them who you are".
//
// Submitting does NOT create an account: it validates, checks the email is
// free, runs reCAPTCHA and sends the 6-digit code. The account is created at
// the verification step (SignupVerifyEmailPage).

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import { getSubmissionFlowApi, isMockApi } from "../../../shared/api/submission";
import { IClassicSessionDto, ISubmissionDraftDto } from "../../../shared/api/submission/types";
import {
  emailValidation,
  getSuggestedEmail,
  passwordValidation,
} from "../../../helpers/validations";
import {
  EMAIL_MAX_LENGTH,
  isValidName,
  isWithinEmailLength,
  MIN_PASSWORD_LENGTH,
  NAME_MAX_LENGTH,
} from "../common/validation";
import { isSubmissionAuthed, setSubmissionAuth } from "../../../helpers/submissionAuth";
import { FIRST_SIGNED_IN_STEP, flowLoginUrl, resumePath } from "../common/flowLogin";
import {
  CLASSIC_SESSION_WAIT_MS,
  dismissClassicSession,
  isClassicSessionDismissed,
  withTimeout,
} from "../common/classicSession";
import { applySignedInLanguage, ensureFlowLanguage } from "../common/flowLanguage";
import { parseSubmissionParams } from "../common/submissionParams";
import { forgetTabAccount } from "../common/tabAccount";
import { forgetMembershipAsked } from "../../../models/submissionStep4Model";
import { recaptchaTokenWhenReady } from "../common/recaptchaToken";
import { fallbackTo, hasFlowContext } from "../common/flowGuard";
import { classicInvitationPath } from "../common/agentInvitation";
import { getTrackingData } from "helpers";
import { trackSubmissionEvent } from "../analytics";
import {
  MEMBERSHIP_DARK_BLUE,
  membershipPrimary,
  membershipSecondary,
} from "../common/membershipButton";

interface Fields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

type Errors = { [K in keyof Fields]?: string };

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const SignupFormInner: React.FC<Props> = ({
  history,
  location,
  breakdownId,
  roleId,
  geoGuess,
  loading,
  errorCode,
  errorMessage,
  startSignup,
  loadGeoGuess,
  refreshGate,
  clearError,
  resetSignup,
  resetStep4,
  setAccount,
  ensureDraft,
}) => {
  const [fields, setFields] = useState<Fields>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaUnavailable, setRecaptchaUnavailable] = useState(false);
  // Honeypot: hidden from humans, filled by bots (mirrors the backend's
  // `Information` field convention).
  const [honeypot, setHoneypot] = useState("");
  // The email the server said already has an account: Log in pre-fills it.
  const [takenEmail, setTakenEmail] = useState("");
  // An actor logged in on the classic site: "Continue as" instead of the form (decided
  // 2026-10-30). undefined while asking; null when there is none, it isn't an actor's, or this
  // tab said "Not you?".
  const [classic, setClassic] = useState<IClassicSessionDto | null | undefined>(() =>
    isClassicSessionDismissed() ? null : undefined
  );
  const [continuing, setContinuing] = useState(false);

  const recaptcha = useGoogleReCaptcha();
  // The hook's latest value: a press made while the script loads waits for it (recaptchaToken.ts).
  const recaptchaRef = useRef(recaptcha);
  recaptchaRef.current = recaptcha;
  // Left while Continue waits for that token (Back): no signup starts for a page no one is on,
  // and nobody is taken to the code screen from wherever they went.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    // Every flow URL must carry `bd`. Without it there is nothing to submit
    // to, so hand the visitor to the traditional actor signup.
    if (!hasFlowContext({ search: location.search, breakdownId })) {
      history.replace(fallbackTo(location.search));
      return;
    }
    // Same guard the later steps have: after the account exists, Back must not
    // land on a blank form whose re-submit would clobber the live session.
    if (isSubmissionAuthed()) {
      history.replace(`/submission/flow/step/2${location.search}`);
      return;
    }
    // An agent's invitation is the classic signup's job (common/agentInvitation.ts).
    const invitation = classicInvitationPath(location.search, getTrackingData().queryParams);
    if (invitation) {
      history.replace(invitation);
      return;
    }
    if (classic === undefined) {
      withTimeout(getSubmissionFlowApi().findClassicSession(), CLASSIC_SESSION_WAIT_MS, null).then(
        (found) => {
          if (found) trackSubmissionEvent("sf_classic_session_offered", {});
          setClassic(found);
        }
      );
    }
    trackSubmissionEvent("sf_signup_form_viewed", { breakdownId, roleId });
    // A stale error from a later step must not render under this step's copy.
    clearError();
    // Deadline re-check on step mount, and geo guess for the account defaults.
    refreshGate({ history, search: location.search, entry: true });
    if (!geoGuess) loadGeoGuess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key: keyof Fields) => (e: any) => {
    // Read the value BEFORE the state updater runs. React 16 pools synthetic
    // events, so by the time a functional updater is invoked (during the next
    // render) `e.target` has been recycled to null.
    const { value } = e.target;
    setFields((f) => ({ ...f, [key]: value }));
    if (key === "email" && emailSuggestion) setEmailSuggestion("");
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // Set when the typed domain is a known misspelling ("gmail.con"), so the
  // rejection can name the address the user meant instead of dead-ending on
  // "Email is not valid".
  const [emailSuggestion, setEmailSuggestion] = useState("");

  const validate = (): boolean => {
    const next: Errors = {};
    const t = i18next.t.bind(i18next);

    if (!fields.firstName.trim()) next.firstName = t("Name is required");
    else if (!isValidName(fields.firstName)) next.firstName = t("Name is not valid");

    if (!fields.lastName.trim()) next.lastName = t("Last name is required");
    else if (!isValidName(fields.lastName)) next.lastName = t("Last name is not valid");

    // emailValidation() also screens the domain against the shared rejection
    // list (emailDomainBlacklist.json + emailDomainTypos.json), so disposable
    // providers AND misspellings of the real ones are refused here — the same
    // data the API validates against.
    const emailError = emailValidation(fields.email.trim(), t);
    // emailValidation already answers a misspelled provider with
    // "Did you mean ...?"; the suggestion is kept for the one-tap fix button.
    setEmailSuggestion(getSuggestedEmail(fields.email));
    if (emailError) {
      next.email = emailError;
    } else if (!isWithinEmailLength(fields.email)) {
      next.email = t("Email is not valid");
    }

    // Same validator the traditional actor signup uses (ActorCredentialStep),
    // so the rule and the message are identical across both flows.
    const passwordError = passwordValidation(fields.password, MIN_PASSWORD_LENGTH, t);
    if (passwordError) next.password = passwordError;

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Bypass is keyed on MOCK MODE, never on "no site key configured" — the
  // latter would silently ship a fake token to the real API in any build whose
  // env forgot the key.
  const getRecaptchaToken = async (): Promise<string> => {
    if (isMockApi()) return "mock-recaptcha-token";
    // Local real-API runs have no site key. The dev server (never a build) may
    // send a placeholder; the API accepts it only outside production and only
    // for company test addresses, and rejects it as recaptchaFailed otherwise.
    if (process.env.NODE_ENV === "development" && process.env.REACT_APP_RECAPTCHA_DEV_BYPASS === "true")
      return "dev-recaptcha-bypass";
    return recaptchaTokenWhenReady(
      () => recaptchaRef.current && recaptchaRef.current.executeRecaptcha,
      "submission_signup"
    );
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (submitting || loading) return;
    if (!validate()) return;

    setSubmitting(true);
    const recaptchaToken = await getRecaptchaToken();
    if (!mounted.current) return;
    if (!recaptchaToken) {
      // Real API + no usable token (missing site key, script not loaded after the
      // wait, or execute threw): fail loudly instead of posting an empty/fake token.
      setSubmitting(false);
      setRecaptchaUnavailable(true);
      return;
    }
    try {
      await startSignup({
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        email: fields.email.trim(),
        password: fields.password,
        recaptchaToken,
        breakdownId: breakdownId || undefined,
        roleId: roleId || undefined,
        geoCountryCode: geoGuess ? geoGuess.countryCode : undefined,
        geoCityId: geoGuess ? geoGuess.cityId : undefined,
        geoRegionCode: geoGuess ? geoGuess.regionCode : undefined,
        information: honeypot,
        trackingData: getTrackingData(),
      });
      trackSubmissionEvent("sf_signup_code_sent", { breakdownId, roleId });
      // Left while it was asked: the code is on its way, but the visitor stays where they went.
      if (mounted.current) history.push(`/submission/flow/verify-email${location.search}`);
    } catch (err) {
      if (!mounted.current) return;
      // Field-level mapping for the errors the user can actually fix here.
      const code = (err as any) && (err as any).errorCode;
      if (code === "agentInvitation") {
        // The server saw an invitation the page missed: same destination.
        history.replace(
          classicInvitationPath(location.search, getTrackingData().queryParams) || "/actor/1?ac=true"
        );
        return;
      }
      // Step 1 refused: before any account, only GA4 can see it (docs/35 S3).
      trackSubmissionEvent("sf_step_failed", { step: "signup", reason: code || "unknown" });
      if (code === "emailAlreadyRegistered") {
        setTakenEmail(fields.email.trim());
        setErrors({ email: i18next.t("This email already has an account") });
      } else if (code === "invalidEmail") {
        setErrors({ email: i18next.t("Email is not valid") });
      } else if (code === "weakPassword") {
        setErrors({
          password: i18next.t("Must contain at least 6 characters, 1 letter and 1 number"),
        });
      }
      // Only the failure path resets the flag: on success this component has
      // already been unmounted by the push above, and setState would warn.
      setSubmitting(false);
    }
  };

  // Continue as them: this tab signs in with the classic session, as the login's hand-off does
  // (SubmissionContinuePage), and resumes this casting call where their submission is.
  const onContinueClassic = async () => {
    if (continuing) return;
    setContinuing(true);
    trackSubmissionEvent("sf_classic_session_continue", {});
    forgetMembershipAsked();
    try {
      const signIn = await getSubmissionFlowApi().continueClassicSession();
      setSubmissionAuth(signIn);
      // Nothing of anyone signed in here before carries over (tabAccount.ts).
      forgetTabAccount();
      resetSignup();
      resetStep4();
      setAccount({ clientId: signIn.clientId, accountDefaults: null });
      applySignedInLanguage();
      ensureFlowLanguage();
      const { bd, roleid } = parseSubmissionParams(location.search);
      const draft = await ensureDraft({ breakdownId: bd, roleId: roleid });
      history.replace(`${resumePath(null, draft)}${location.search}`);
    } catch (e) {
      // The session ended since: the usual form, with Log in.
      trackSubmissionEvent("sf_classic_session_failed", {});
      setContinuing(false);
      setClassic(null);
    }
  };

  const onNotYou = () => {
    trackSubmissionEvent("sf_classic_session_not_you", {});
    dismissClassicSession();
    setClassic(null);
  };

  if (classic === undefined) {
    return (
      <SubmissionLayout step={1}>
        <Checking aria-busy="true">{i18next.t("One moment…")}</Checking>
      </SubmissionLayout>
    );
  }

  if (classic) {
    return (
      <SubmissionLayout step={1}>
        <ClassicCard>
          <ClassicLabel>{i18next.t("You're logged in as")}</ClassicLabel>
          <ClassicName>
            {[classic.firstName, classic.lastName].filter((part) => !!part).join(" ")}
            <ClassicId>
              {" · "}
              {i18next.t("Workbook ID {{id}}", { id: classic.clientId })}
            </ClassicId>
          </ClassicName>
          <PrimaryButton type="button" onClick={onContinueClassic} disabled={continuing}>
            {continuing
              ? i18next.t("One moment…")
              : i18next.t("Continue as {{name}}", { name: classic.firstName })}
          </PrimaryButton>
          <NotYouButton type="button" onClick={onNotYou} disabled={continuing}>
            {i18next.t("Not you? Use another account")}
          </NotYouButton>
        </ClassicCard>
      </SubmissionLayout>
    );
  }

  // Continue under way (its token can wait for the script): the fields are read-only meanwhile,
  // so what was checked is what is sent.
  const busy = submitting || loading;
  // Log in comes back to step 2 of this submission (Phase D). When the email already has an
  // account, it is pre-filled: the login takes an email or a Workbook ID.
  const loginUrl = flowLoginUrl(location.search, FIRST_SIGNED_IN_STEP, takenEmail);

  return (
    <SubmissionLayout step={1}>
      <Title>{i18next.t("Tell them who you are")}</Title>
      <SubTitle>{i18next.t("Casting needs a name and a way to reach you.")}</SubTitle>

      <form onSubmit={onSubmit} noValidate>
        <Field>
          <Input
            value={fields.firstName}
            readOnly={busy}
            onChange={setField("firstName")}
            placeholder={i18next.t("First name")}
            autoComplete="given-name"
            maxLength={NAME_MAX_LENGTH}
            hasError={!!errors.firstName}
            aria-label={i18next.t("First name")}
            aria-invalid={!!errors.firstName}
          />
          {errors.firstName && <FieldError role="alert">{errors.firstName}</FieldError>}
        </Field>

        <Field>
          <Input
            value={fields.lastName}
            readOnly={busy}
            onChange={setField("lastName")}
            placeholder={i18next.t("Last name")}
            autoComplete="family-name"
            maxLength={NAME_MAX_LENGTH}
            hasError={!!errors.lastName}
            aria-label={i18next.t("Last name")}
            aria-invalid={!!errors.lastName}
          />
          {errors.lastName && <FieldError role="alert">{errors.lastName}</FieldError>}
        </Field>

        <Field>
          <Input
            type="email"
            value={fields.email}
            readOnly={busy}
            onChange={setField("email")}
            placeholder={i18next.t("Email")}
            autoComplete="email"
            inputMode="email"
            maxLength={EMAIL_MAX_LENGTH}
            hasError={!!errors.email}
            aria-label={i18next.t("Email")}
            aria-invalid={!!errors.email}
          />
          {errors.email && <FieldError role="alert">{errors.email}</FieldError>}
          {errors.email && emailSuggestion && (
            <SuggestionButton
              type="button"
              onClick={() => {
                setFields((f) => ({ ...f, email: emailSuggestion }));
                setErrors((prev) => ({ ...prev, email: undefined }));
                setEmailSuggestion("");
              }}
            >
              {i18next.t("Use {{email}}", { email: emailSuggestion })}
            </SuggestionButton>
          )}
        </Field>

        <Field>
          <PasswordRow>
            <Input
              type={showPassword ? "text" : "password"}
              value={fields.password}
              readOnly={busy}
              onChange={setField("password")}
              placeholder={i18next.t("Password")}
              autoComplete="new-password"
              hasError={!!errors.password}
              aria-label={i18next.t("Password")}
              aria-invalid={!!errors.password}
            />
            <PeekButton
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? i18next.t("Hide password") : i18next.t("Show password")}
            >
              {showPassword ? "🙈" : "👁"}
            </PeekButton>
          </PasswordRow>
          {errors.password ? (
            <FieldError role="alert">{errors.password}</FieldError>
          ) : (
            <Hint>
              {i18next.t("You must have at least 6 characters, 1 letter and 1 number")}
            </Hint>
          )}
        </Field>

        {/* Honeypot — visually hidden, never announced, never tabbable. */}
        <HoneypotWrapper aria-hidden="true">
          <input
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </HoneypotWrapper>

        {recaptchaUnavailable && (
          <FormError role="alert">
            {i18next.t("We couldn't verify that you're human. Please try again.")}
          </FormError>
        )}

        {errorCode && !errors.email && !errors.password && (
          <FormError role="alert">
            {errorCode === "network"
              ? errorMessage
              : errorCode === "recaptchaLowScore" || errorCode === "recaptchaFailed"
              ? i18next.t("We couldn't verify that you're human. Please try again.")
              : errorCode === "rateLimited"
              ? i18next.t("Too many requests. Please try again in a moment.")
              : i18next.t("Something went wrong. Please try again.")}
          </FormError>
        )}

        <PrimaryButton type="submit" disabled={busy}>
          {busy ? i18next.t("One moment…") : i18next.t("Continue")}
        </PrimaryButton>
      </form>

      <LoginRow>
        <span>{i18next.t("Already in theWorkbook?")}</span>
      </LoginRow>
      <SecondaryLink href={loginUrl}>{i18next.t("Log in")}</SecondaryLink>

      <Legal>
        {i18next.t("By signing up, you agree to our")}{" "}
        <LegalLink
          href={`${process.env.REACT_APP_CWB_Site}/${process.env.REACT_APP_CWB_Terms_Path}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {i18next.t("Terms of Use")}
        </LegalLink>{" "}
        {i18next.t("and")}{" "}
        <LegalLink
          href={`${process.env.REACT_APP_CWB_Site}/${process.env.REACT_APP_CWB_Privacy_Path}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {i18next.t("Privacy Policy")}
        </LegalLink>
      </Legal>

      {isMockApi() && (
        <MockHint>
          mock: any valid form works · taken@theworkbook.com → 409 · code is 123456
        </MockHint>
      )}
    </SubmissionLayout>
  );
};

// reCAPTCHA v3 needs a provider above the hook. With no site key (mock env)
// we skip the provider entirely so no Google script is loaded.
const SignupFormPage: React.FC<Props> = (props) => {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (!siteKey) return <SignupFormInner {...props} />;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
      <SignupFormInner {...props} />
    </GoogleReCaptchaProvider>
  );
};

const Title = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: #122640;
  text-align: center;
  margin: 28px 0 8px;
`;

const SubTitle = styled.p`
  font-size: 15px;
  color: #53627c;
  text-align: center;
  margin: 0 0 24px;
`;

const Field = styled.div`
  margin-bottom: 12px;
`;

const Input: any = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 46px;
  border: 1px solid ${(p: any) => (p.hasError ? "#c7053f" : "#c9d3e0")};
  border-radius: 5px;
  padding: 0 14px;
  font-size: 15px;
  outline: 0;

  &:focus {
    border-color: #0f9bf0;
  }
`;

const PasswordRow = styled.div`
  position: relative;
`;

const PeekButton = styled.button`
  position: absolute;
  right: 6px;
  top: 0;
  height: 46px;
  width: 44px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #53627c;
  margin-top: 6px;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: #c7053f;
  margin-top: 6px;
`;

const SuggestionButton = styled.button`
  margin-top: 6px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 600;
  color: #0d6efd;
  text-decoration: underline;
  cursor: pointer;

  &:focus {
    outline: 2px solid #0d6efd;
    outline-offset: 2px;
  }
`;

const FormError = styled.div`
  font-size: 13px;
  color: #c7053f;
  margin: 12px 0;
  text-align: center;
`;

const HoneypotWrapper = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const PrimaryButton = styled.button`
  width: 100%;
  ${membershipPrimary}
  padding: 14px 0;
  margin-top: 12px;
  min-height: 48px;
`;

const Checking = styled.p`
  text-align: center;
  font-size: 15px;
  color: #53627c;
  margin: 48px 0;
`;

const ClassicCard = styled.div`
  margin-top: 28px;
  padding: 24px;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  background: #ffffff;
`;

const ClassicLabel = styled.h1`
  font-size: 15px;
  font-weight: 400;
  color: #53627c;
  margin: 0 0 6px;
`;

const ClassicName = styled.p`
  font-size: 20px;
  font-weight: 700;
  color: #122640;
  margin: 0 0 12px;
  overflow-wrap: anywhere;
`;

const ClassicId = styled.span`
  font-size: 15px;
  font-weight: 400;
  color: #53627c;
`;

const NotYouButton = styled.button`
  display: block;
  margin: 18px auto 0;
  padding: 6px 4px;
  background: none;
  border: 0;
  font-size: 14px;
  color: ${MEMBERSHIP_DARK_BLUE};
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #0584d7;
    outline-offset: 2px;
  }
`;

const LoginRow = styled.div`
  text-align: center;
  font-size: 13px;
  color: #53627c;
  margin-top: 32px;
`;

const SecondaryLink = styled.a`
  display: block;
  width: 100%;
  text-align: center;
  ${membershipSecondary}
  padding: 13px 0;
  margin-top: 10px;
  text-decoration: none;

  /* App.tsx colours every link (Wrapper a), which outranks one class. */
  &&,
  &&:hover {
    color: ${MEMBERSHIP_DARK_BLUE};
  }
`;

const Legal = styled.div`
  font-size: 11px;
  color: #53627c;
  text-align: center;
  margin-top: 24px;
  line-height: 18px;
`;

const LegalLink = styled.a`
  color: #122640;
  font-weight: 700;
  text-decoration: none;
`;

const MockHint = styled.div`
  margin-top: 20px;
  font-size: 11px;
  color: #8a94a6;
  font-family: monospace;
  text-align: center;
`;

interface StateProps {
  breakdownId: string | null;
  roleId: string | null;
  geoGuess: iRootState["submissionFlowModel"]["geoGuess"];
  loading: boolean;
  errorCode: string | null;
  errorMessage: string | null;
}

interface DispatchProps {
  startSignup: (payload: any) => Promise<any>;
  loadGeoGuess: () => Promise<any>;
  refreshGate: (payload: { history: any; search?: string; entry?: boolean }) => Promise<any>;
  clearError: () => void;
  resetSignup: () => void;
  resetStep4: () => void;
  setAccount: (payload: { clientId: number; accountDefaults: null }) => void;
  ensureDraft: (payload: { breakdownId: string | null; roleId: string | null }) => Promise<ISubmissionDraftDto | null>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  breakdownId: state.submissionFlowModel.breakdownId,
  roleId: state.submissionFlowModel.roleId,
  geoGuess: state.submissionFlowModel.geoGuess,
  loading: state.submissionSignupModel.loading,
  errorCode: state.submissionSignupModel.errorCode,
  errorMessage: state.submissionSignupModel.errorMessage,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  startSignup: dispatch.submissionSignupModel.startSignup,
  loadGeoGuess: dispatch.submissionFlowModel.loadGeoGuess,
  refreshGate: dispatch.submissionFlowModel.refreshGate,
  clearError: dispatch.submissionSignupModel.clearError,
  resetSignup: dispatch.submissionSignupModel.reset,
  resetStep4: dispatch.submissionStep4Model.reset,
  setAccount: dispatch.submissionSignupModel.setAccount,
  ensureDraft: dispatch.submissionSignupModel.ensureDraft,
});

export default connect(mapStateToProps, mapDispatchToProps)(SignupFormPage);
